import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import AntLocalization from '../../utils/AntLocalization';
import AntCondition from '../../utils/AntCondition';
import ServiceCard from './ServiceCard';
import './index.scss';

// ─── Default column configs ──────────────────────────────────
var DEFAULT_COLUMNS = [
    {
        key: 'upcoming',
        label: { vi: 'Sắp đến', en: 'Upcoming', fr: 'À venir', es: 'Próximos', id: 'Mendatang', ja: '来店予定', zh: '即将到来', ko: '예정', hi: 'आगामी', ar: 'قادم' },
        icon: '○',
        color: '#3B82F6',
        filter: function (item, settings) {
            var status = item.appointment_status || item.status || '';
            if (status !== 'Release') return false;

            // Check if within upcoming window (next 60 minutes)
            if (item.appointment_hour) {
                var now = new Date();
                var nowMinutes = now.getHours() * 60 + now.getMinutes();
                var appointmentMinutes = parseTimeMinutes(item.appointment_hour);
                if (appointmentMinutes == null) return true;
                var diff = appointmentMinutes - nowMinutes;
                return diff >= -30 && diff <= 60;
            }
            return true;
        }
    },
    {
        key: 'waiting',
        label: { vi: 'Đang chờ', en: 'Waiting', fr: 'En attente', es: 'En espera', id: 'Menunggu', ja: '待機中', zh: '等待中', ko: '대기 중', hi: 'प्रतीक्षारत', ar: 'بالانتظار' },
        icon: '◔',
        color: '#F59E0B',
        filter: function (item) {
            var status = item.appointment_status || item.status || '';
            if (status !== 'Open') return false;
            // Open but not started serving yet
            return !item.serve_start_at;
        }
    },
    {
        key: 'serving',
        label: { vi: 'Đang phục vụ', en: 'In Service', fr: 'En service', es: 'En servicio', id: 'Dilayani', ja: '対応中', zh: '服务中', ko: '서비스 중', hi: 'सेवा में', ar: 'قيد الخدمة' },
        icon: '●',
        color: '#8B5CF6',
        filter: function (item) {
            var status = item.appointment_status || item.status || '';
            if (status !== 'Open') return false;
            // Started serving
            return !!item.serve_start_at && !item.serve_end_at;
        }
    },
    {
        key: 'completed',
        label: { vi: 'Hoàn thành', en: 'Completed', fr: 'Terminé', es: 'Completado', id: 'Selesai', ja: '完了', zh: '已完成', ko: '완료', hi: 'पूर्ण', ar: 'مكتمل' },
        icon: '◉',
        color: '#10B981',
        filter: function (item) {
            var status = item.appointment_status || item.status || '';
            return status === 'Close' || status === 'Closed';
        }
    }
];

// ─── Default actions per column ──────────────────────────────
var DEFAULT_ACTIONS = [
    {
        name: 'actionConfirm',
        column: 'upcoming',
        text: { vi: 'Check-in', en: 'Check-in', fr: 'Check-in', es: 'Check-in', id: 'Check-in', ja: '受付', zh: '签到', ko: '체크인', hi: 'चेक-इन', ar: 'تسجيل وصول' },
        variant: 'primary'
    },
    {
        name: 'actionCancel',
        column: 'upcoming',
        text: { vi: 'Hủy', en: 'Cancel', fr: 'Annuler', es: 'Cancelar', id: 'Batal', zh: '取消', ko: '취소', hi: 'रद्द करें', ar: 'إلغاء', ja: '取消' },
        variant: 'danger'
    },
    {
        name: 'actionServeStart',
        column: 'waiting',
        text: { vi: 'Bắt đầu', en: 'Start', fr: 'Début', es: 'Inicio', id: 'Mulai', ja: '開始', zh: '开始', ko: '시작', hi: 'आरंभ', ar: 'البدء' },
        variant: 'primary'
    },
    {
        name: 'actionCancel',
        column: 'waiting',
        text: { vi: 'Hủy', en: 'Cancel', fr: 'Annuler', es: 'Cancelar', id: 'Batal', zh: '取消', ko: '취소', hi: 'रद्द करें', ar: 'إلغاء', ja: '取消' },
        variant: 'danger'
    },
    {
        name: 'actionCheckin',
        column: 'serving',
        text: { vi: 'Hoàn thành', en: 'Complete', fr: 'Terminé', es: 'Completado', id: 'Selesai', ja: '完了', zh: '已完成', ko: '완료', hi: 'पूर्ण', ar: 'مكتمل' },
        variant: 'success'
    }
];

// ─── Mock Data ───────────────────────────────────────────────────
var MOCK_APPOINTMENTS = (function () {
    var now = new Date();
    var todayStr = now.toISOString().split('T')[0];
    var h = now.getHours();
    return [
        { id: 'apt-1', code: 'APT-001', appointment_date: todayStr, appointment_hour: (h + 0.5), appointment_status: 'Release', entity_name: 'Chị Mai', entity_phone: '0901234567', queue_code: 'A01', items_text: 'Gội đầu, Massage vai', serve_entity_name: 'Nguyễn Thị Lan', serve_place_name: 'Phòng VIP 1' },
        { id: 'apt-2', code: 'APT-002', appointment_date: todayStr, appointment_hour: (h - 0.5), appointment_status: 'Release', entity_name: 'Anh Tuấn', entity_phone: '0909876543', queue_code: 'A02', items_text: 'Cắt tóc nam', serve_entity_name: 'Trần Văn Minh', serve_place_name: 'Ghế 3' },
        { id: 'apt-3', code: 'APT-003', appointment_date: todayStr, appointment_hour: (h - 1), appointment_status: 'Open', entity_name: 'Chị Hoa', entity_phone: '0912345678', queue_code: 'A03', items_text: 'Nhuộm tóc', serve_entity_name: 'Nguyễn Thị Lan', serve_place_name: 'Ghế 1', checked_in_at: new Date(now - 1800000).toISOString() },
        { id: 'apt-4', code: 'APT-004', appointment_date: todayStr, appointment_hour: (h - 1.5), appointment_status: 'Open', entity_name: 'Anh Long', entity_phone: '0923456789', queue_code: 'A04', items_text: 'Massage body 60p', serve_entity_name: 'Phạm Hồng Anh', serve_place_name: 'Phòng VIP 2', checked_in_at: new Date(now - 3600000).toISOString(), serve_start_at: new Date(now - 2400000).toISOString() },
        { id: 'apt-5', code: 'APT-005', appointment_date: todayStr, appointment_hour: (h - 2), appointment_status: 'Open', entity_name: 'Chị Linh', entity_phone: '0934567890', queue_code: 'A05', items_text: 'Facial chăm sóc da', serve_entity_name: 'Lê Thúy Hằng', serve_place_name: 'Phòng thường 1', checked_in_at: new Date(now - 5400000).toISOString(), serve_start_at: new Date(now - 4200000).toISOString() },
        { id: 'apt-6', code: 'APT-006', appointment_date: todayStr, appointment_hour: (h - 3), appointment_status: 'Close', entity_name: 'Chị Ngọc', entity_phone: '0945678901', queue_code: 'A06', items_text: 'VIP Spa combo', serve_entity_name: 'Phạm Hồng Anh', serve_place_name: 'Phòng VIP 1', checked_in_at: new Date(now - 10800000).toISOString(), serve_start_at: new Date(now - 9000000).toISOString(), serve_end_at: new Date(now - 3600000).toISOString() },
        { id: 'apt-7', code: 'APT-007', appointment_date: todayStr, appointment_hour: (h - 2.5), appointment_status: 'Close', entity_name: 'Chị Yến', entity_phone: '0956789012', queue_code: 'A07', items_text: 'Chăm sóc da mặt', serve_entity_name: 'Lê Thúy Hằng', serve_place_name: 'Phòng thường 1', checked_in_at: new Date(now - 7200000).toISOString(), serve_start_at: new Date(now - 6000000).toISOString(), serve_end_at: new Date(now - 1800000).toISOString() }
    ];
})();

function parseTimeMinutes(value) {
    if (value == null) return null;
    if (typeof value === 'number') {
        if (value > 100000) {
            var d = new Date(value * 1000);
            return d.getHours() * 60 + d.getMinutes();
        }
        if (value > 1440) {
            // Seconds since midnight
            return Math.floor(value / 60);
        }
        return value;
    }
    if (typeof value === 'string') {
        var parts = value.split(':');
        return parseInt(parts[0], 10) * 60 + parseInt(parts[1] || '0', 10);
    }
    return null;
}

// ─── Column Component ─────────────────────────────────────────
function BoardColumn(props) {
    var {
        column,
        items,
        showTimer,
        showQueueCode,
        actions,
        onAction,
        onCardClick,
        cardFields
    } = props;

    var label = column.label ? AntLocalization.translateText(column.label) : column.key;
    var count = items.length;

    return (
        <div className="board-column">
            <div className="column-header" style={{ borderTopColor: column.color || '#94a3b8' }}>
                <div className="column-title">
                    {column.icon && <span className="column-icon">{column.icon}</span>}
                    <span className="column-label">{label}</span>
                    <span className="column-count">{count}</span>
                </div>
            </div>
            <div className="column-body">
                {items.length === 0 && (
                    <div className="column-empty">
                        {AntLocalization.translateText({ vi: 'Trống', en: 'Empty', fr: 'Libre', es: 'Libre', id: 'Kosong', ja: '空き', zh: '空闲', ko: '빈', hi: 'खाली', ar: 'شاغر' })}
                    </div>
                )}
                {items.map(function (item) {
                    // Check if upcoming (within 15 minutes)
                    var isUpcoming = false;
                    if (column.key === 'upcoming' && item.appointment_hour) {
                        var now = new Date();
                        var nowMin = now.getHours() * 60 + now.getMinutes();
                        var apptMin = parseTimeMinutes(item.appointment_hour);
                        if (apptMin != null && (apptMin - nowMin) <= 15 && (apptMin - nowMin) >= 0) {
                            isUpcoming = true;
                        }
                    }

                    return (
                        <ServiceCard
                            key={item.id || item.code}
                            item={item}
                            column={column}
                            showTimer={showTimer}
                            showQueueCode={showQueueCode}
                            actions={actions}
                            onAction={onAction}
                            onClick={onCardClick}
                            cardFields={cardFields}
                            upcoming={isUpcoming}
                        />
                    );
                })}
            </div>
        </div>
    );
}

// ═══════════════════════════════════════════════════════════════
// ─── MAIN COMPONENT ──────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════
export default function AntServiceBoard(props) {
    var {
        // Data
        API,
        items: itemsProp,

        // Config
        columns: columnsProp,
        actions: actionsProp,
        statusField = 'appointment_status',
        timeField = 'appointment_hour',
        session,

        // Display
        showTimer = true,
        showQueueCode = true,
        showRefreshTimer = true,
        showWalkIn = true,
        compact = false,
        cardFields,

        // Refresh
        refreshInterval = 30000,

        // Events
        onAction,
        onCardClick,
        onWalkIn,

        // Filtering
        searchConditions,

        // Mock & Visibility
        mockMode = false,
        visible
    } = props;

    var [items, setItems] = useState(itemsProp || []);
    var [loading, setLoading] = useState(false);
    var [lastRefresh, setLastRefresh] = useState(null);
    var [refreshCountdown, setRefreshCountdown] = useState(0);
    var refreshTimerRef = useRef(null);
    var countdownRef = useRef(null);

    // ─── Columns config ───────────────────────────────────────
    var columns = useMemo(function () {
        return columnsProp || DEFAULT_COLUMNS;
    }, [columnsProp]);

    var actions = useMemo(function () {
        return actionsProp || DEFAULT_ACTIONS;
    }, [actionsProp]);

    // ─── Load data ────────────────────────────────────────────
    var loadData = useCallback(async function () {
        if (mockMode) {
            setItems(MOCK_APPOINTMENTS);
            setLastRefresh(new Date());
            return;
        }
        if (!API) return;
        setLoading(true);
        try {
            var params = { pageSize: 200 };
            if (searchConditions) {
                params.searchConditions = Object.assign({}, searchConditions);
            } else {
                params.searchConditions = {};
            }

            // Default: today's appointments
            var today = new Date();
            var todayStr = today.getFullYear() + '-' +
                ((today.getMonth() + 1) < 10 ? '0' : '') + (today.getMonth() + 1) + '-' +
                (today.getDate() < 10 ? '0' : '') + today.getDate();

            if (!params.searchConditions.appointment_date) {
                params.searchConditions.appointment_date = todayStr;
            }

            // Exclude cancelled by default
            if (!params.searchConditions[statusField]) {
                params.searchConditions[statusField] = { type: '!=', value: 'Cancel' };
            }

            var result = await API.getList(params);
            if (result && result.rows) {
                setItems(result.rows);
            }
            setLastRefresh(new Date());
        } catch (e) {
            // silent
        }
        setLoading(false);
    }, [API, searchConditions, statusField, mockMode]);

    // ─── Sync itemsProp ───────────────────────────────────────
    useEffect(function () {
        if (itemsProp) {
            setItems(itemsProp);
        } else if (API) {
            loadData();
        }
    }, [itemsProp, API, loadData]);

    // ─── Auto refresh ─────────────────────────────────────────
    useEffect(function () {
        if (!API || itemsProp || !refreshInterval) return;

        refreshTimerRef.current = setInterval(function () {
            loadData();
        }, refreshInterval);

        return function () {
            if (refreshTimerRef.current) clearInterval(refreshTimerRef.current);
        };
    }, [API, itemsProp, refreshInterval, loadData]);

    // ─── Refresh countdown ────────────────────────────────────
    useEffect(function () {
        if (!showRefreshTimer || !refreshInterval || !lastRefresh) return;

        var updateCountdown = function () {
            var elapsed = Date.now() - lastRefresh.getTime();
            var remaining = Math.max(0, Math.ceil((refreshInterval - elapsed) / 1000));
            setRefreshCountdown(remaining);
        };

        updateCountdown();
        countdownRef.current = setInterval(updateCountdown, 1000);

        return function () {
            if (countdownRef.current) clearInterval(countdownRef.current);
        };
    }, [showRefreshTimer, refreshInterval, lastRefresh]);

    // ─── Distribute items into columns ────────────────────────
    var columnItems = useMemo(function () {
        var result = {};
        for (var c = 0; c < columns.length; c++) {
            result[columns[c].key] = [];
        }

        for (var i = 0; i < items.length; i++) {
            var item = items[i];
            for (var j = 0; j < columns.length; j++) {
                var col = columns[j];
                if (col.filter && col.filter(item, session ? session.settings : {})) {
                    result[col.key].push(item);
                    break;
                }
            }
        }

        // Sort each column by appointment_hour
        for (var key in result) {
            result[key].sort(function (a, b) {
                var ta = parseTimeMinutes(a[timeField]) || 0;
                var tb = parseTimeMinutes(b[timeField]) || 0;
                return ta - tb;
            });
        }

        return result;
    }, [items, columns, timeField, session]);

    // ─── Total count ──────────────────────────────────────────
    var totalCount = useMemo(function () {
        var total = 0;
        for (var key in columnItems) {
            total += columnItems[key].length;
        }
        return total;
    }, [columnItems]);

    // ─── Handle action ────────────────────────────────────────
    var handleAction = useCallback(function (actionName, item) {
        if (onAction) {
            onAction(actionName, item, function () {
                // Callback to refresh after action
                if (API && !itemsProp) loadData();
            });
        }
    }, [onAction, API, itemsProp, loadData]);

    // ─── Today label ──────────────────────────────────────────
    var todayLabel = useMemo(function () {
        var today = new Date();
        var dd = today.getDate();
        var mm = today.getMonth() + 1;
        var yyyy = today.getFullYear();
        var dayNames = {
            vi: ['Chủ nhật', 'Thứ 2', 'Thứ 3', 'Thứ 4', 'Thứ 5', 'Thứ 6', 'Thứ 7'],
            en: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
        };
        var lang = 'vi';
        try { lang = AntLocalization.getLanguage() || 'vi'; } catch (e) { /* */ }
        var names = dayNames[lang] || dayNames.vi;
        return names[today.getDay()] + ', ' + dd + '/' + mm + '/' + yyyy;
    }, []);

    // ─── Visibility check (after all hooks) ────────────────────
    if (visible && session && !AntCondition.checkAll({}, visible, session)) return null;

    // ─── Render ───────────────────────────────────────────────
    return (
        <div className={'ant-service-board' + (compact ? ' compact' : '')}>
            {/* Header */}
            <div className="board-header">
                <div className="header-left">
                    <span className="header-title">
                        {AntLocalization.translateText({ vi: 'Bảng dịch vụ', en: 'Service Board', fr: 'Tableau service', es: 'Panel servicios', id: 'Papan layanan', ja: 'サービスボード', zh: '服务看板', ko: '서비스 보드', hi: 'सेवा बोर्ड', ar: 'لوحة الخدمات' })}
                    </span>
                    <span className="header-date">{todayLabel}</span>
                    <span className="header-total">
                        {totalCount} {AntLocalization.translateText({ vi: 'lịch hẹn', en: 'appointments', fr: 'rendez-vous', es: 'citas', id: 'janji temu', ja: '件の予約', zh: '个预约', ko: '건 예약', hi: 'अपॉइंटमेंट', ar: 'مواعيد' })}
                    </span>
                </div>
                <div className="header-right">
                    {showWalkIn && onWalkIn && (
                        <button className="walkin-btn" onClick={onWalkIn}>
                            + {AntLocalization.translateText({ vi: 'Walk-in', en: 'Walk-in', fr: 'Sans RDV', es: 'Sin cita', id: 'Walk-in', ja: '飛び込み', zh: '散客', ko: '워크인', hi: 'वॉक-इन', ar: 'بدون موعد' })}
                        </button>
                    )}
                    <button className="refresh-btn" onClick={loadData} disabled={loading}>
                        {loading ? '...' : '↻'}
                    </button>
                    {showRefreshTimer && refreshCountdown > 0 && (
                        <span className="refresh-countdown">{refreshCountdown}s</span>
                    )}
                </div>
            </div>

            {/* Loading overlay */}
            {loading && items.length === 0 && (
                <div className="board-loading">
                    {AntLocalization.translateText({ vi: 'Đang tải...', en: 'Loading...', fr: 'Chargement...', es: 'Cargando...', id: 'Memuat...', ja: '読み込み中...', zh: '加载中...', ko: '로딩 중...', hi: 'लोड हो रहा...', ar: 'تحميل...' })}
                </div>
            )}

            {/* Board columns */}
            <div className="board-body">
                {columns.map(function (col) {
                    return (
                        <BoardColumn
                            key={col.key}
                            column={col}
                            items={columnItems[col.key] || []}
                            showTimer={showTimer}
                            showQueueCode={showQueueCode}
                            actions={actions}
                            onAction={handleAction}
                            onCardClick={onCardClick}
                            cardFields={cardFields}
                        />
                    );
                })}
            </div>
        </div>
    );
}
